<script setup lang="ts" generic="T">
import { computed } from 'vue';

const props = withDefaults(
  defineProps<{
    items: readonly T[];
    getKey?: (item: T, index: number) => number | string;
    maxVisible?: number;
    overlap?: 'badge' | 'none' | 'xs' | 'sm' | 'smd' | 'md';
  }>(),
  {
    maxVisible: 3,
    overlap: 'sm',
    getKey: undefined,
  },
);

const visibleItems = computed(() => props.items.slice(0, Math.max(0, props.maxVisible)));
const overflowCount = computed(() => Math.max(0, props.items.length - visibleItems.value.length));
</script>

<template>
  <div class="inline-flex items-center" :class="props.overlap === 'none' && 'gap-xs'">
    <div
      v-for="(item, index) in visibleItems"
      :key="getKey?.(item, index) ?? index"
      class="relative shrink-0"
      :class="[
        index > 0 && props.overlap === 'xs' && 'ml--xs',
        index > 0 && props.overlap === 'sm' && 'ml--sm',
        index > 0 && props.overlap === 'smd' && 'ml--smd',
        index > 0 && props.overlap === 'md' && 'ml--md',
        index > 0 && props.overlap === 'badge' && 'ml--badge-overlap',
      ]"
      :style="{ zIndex: index }"
    >
      <slot name="item" :item="item" :index="index" />
    </div>
    <div
      v-if="overflowCount > 0"
      class="relative shrink-0"
      :class="[
        visibleItems.length > 0 && props.overlap === 'xs' && 'ml--xs',
        visibleItems.length > 0 && props.overlap === 'sm' && 'ml--sm',
        visibleItems.length > 0 && props.overlap === 'smd' && 'ml--smd',
        visibleItems.length > 0 && props.overlap === 'md' && 'ml--md',
        visibleItems.length > 0 && props.overlap === 'badge' && 'ml--badge-overlap',
      ]"
      :style="{ zIndex: visibleItems.length }"
    >
      <slot name="overflow" :count="overflowCount">
        <span>+{{ overflowCount }}</span>
      </slot>
    </div>
  </div>
</template>
